import { z } from 'zod';
import { useWebSocketServer } from '~/modules/websocket';

const MESSAGE_SCHEMA = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('chat/message'),
        conversation_id: z.number(),
        participant_id: z.number(),
        sender_id: z.number(),
        sender_user_id: z.number(),
        sender_type: z.enum(['Member', 'Professional']),
        is_member_participant: z.boolean(),
        professional_ids: z.array(z.number()),
        is_muted: z.boolean(),
        is_sound_enabled: z.boolean(),
    }),
    z.object({
        type: z.literal('chat/migrate'),
        conversation_id: z.number(),
        original_conversation_id: z.number(),
        participant_id: z.number(),
        sender_id: z.number(),
        sender_user_id: z.number(),
        sender_type: z.enum(['Member', 'Professional']),
        is_member_participant: z.boolean(),
        professional_ids: z.array(z.number()),
        is_muted: z.boolean(),
        is_sound_enabled: z.boolean(),
    }),
    z.object({
        type: z.literal('chat/read'),
        conversation_id: z.number(),
        participant_id: z.number(),
        unread_message_count: z.number(),
        is_enquiry_professional_side: z.boolean(),
    }),
    z.object({
        type: z.literal('chat/status-update'),
        conversation_id: z.number(),
        participant_id: z.number(),
        is_important: z.boolean(),
        is_archived: z.boolean(),
        is_done: z.boolean(),
        is_enquiry_professional_side: z.boolean(),
    }),
    z.object({
        type: z.literal('chat/update'),
        conversation_id: z.number(),
    }),
    z.object({
        type: z.literal('chat/removed'),
        conversation_id: z.number(),
        member_id: z.number().optional(),
        professional_id: z.number().optional(),
    }),
    z.object({
        type: z.literal('chat/message/updated'),
        conversation_id: z.number(),
        message_id: z.number(),
    }),
    z.object({
        type: z.literal('chat/typing'),
        conversation_id: z.number(),
        participant_id: z.number(),
        participant_name: z.string(),
    }),
]);

type MessageType = z.infer<typeof MESSAGE_SCHEMA>;

/**
 * Hook to handle real-time chat with the WebSocket server.
 */
export function useRealTimeChat({
    onMessage,
}: {
    onMessage?: (message: MessageType) => void;
} = {}) {
    const { lastMessage, isConnected, sendJsonMessage } = useWebSocketServer({
        schema: MESSAGE_SCHEMA,
        onMessage,
    });

    return {
        lastMessage,
        isConnected,
        sendJsonMessage,
    };
}
